import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";

import "leaflet/dist/leaflet.css";

import indonesiaGeoJson from "../data/38-Provinsi-Indonesia.json";
import { usePeriode } from "../context/PeriodeContext"; 
import DashboardService from "../../../services/report/DashboardService";
import ReportServiceClass from "../../../services/report/ReportService";

// Instansiasi class ReportService
const ReportService = new ReportServiceClass();

// Helper normalisasi nama
const normalizeName = (str) =>
  String(str || "")
    .toUpperCase()
    .replace(/PROVINSI|KEPULAUAN|KEP\.|DAERAH|ISTIMEWA|KHUSUS/g, "")
    .replace(/[^A-Z0-9]/g, "");

// Helper mengambil nilai angka dari properti objek (Mencakup variasi key)
const getNum = (obj, ...keys) => {
  if (!obj) return 0;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) {
      const val = Number(obj[key]);
      return isNaN(val) ? 0 : val;
    }
  }
  return 0;
};

// Helper pencari data provinsi dengan cakupan nama/kode properti yang lebih luas
const findDataByFeature = (dataArray, featureProps) => {
  if (!Array.isArray(dataArray) || !dataArray.length || !featureProps) return null;

  const rawGeoCode =
    featureProps.KODE_PROV ??
    featureProps.kode_prov ??
    featureProps.KODE ??
    featureProps.kode ??
    featureProps.id;
  
  const geoCodeNum = rawGeoCode !== undefined && rawGeoCode !== null ? parseInt(rawGeoCode, 10) : NaN;

  const geoName = normalizeName(
    featureProps.PROVINSI ??
    featureProps.NM_PROV ??
    featureProps.namobj ??
    featureProps.NAME_1 ?? ""
  );

  // 1. Pencocokan berdasarkan KODE PROVINSI
  let found = dataArray.find((item) => {
    const itemCodeRaw = 
      item.kode_propinsi ?? 
      item.KODE_PROVINSI ?? 
      item.kode_provinsi ??
      item.kode_prov ?? 
      item.kd_prov ??
      item.id_provinsi ??
      item.provinsi_id ??
      item.kd_propinsi;

    if (itemCodeRaw !== undefined && itemCodeRaw !== null) {
      const itemCodeNum = parseInt(itemCodeRaw, 10);
      if (!isNaN(geoCodeNum) && !isNaN(itemCodeNum) && geoCodeNum === itemCodeNum) {
        return true;
      }
    }
    return false;
  });

  // 2. Jika kode tidak cocok, cocokkan berdasarkan NAMA PROVINSI
  if (!found && geoName) {
    found = dataArray.find((item) => {
      const itemName = normalizeName(
        item.propinsi ?? 
        item.PROPINSI ?? 
        item.nama_provinsi ?? 
        item.NAMA_PROVINSI ??
        item.nama_propinsi ??
        item.NM_PROV ?? 
        item.provinsi ??
        item.NM_PROVINSI ??
        ""
      );
      return itemName && (itemName === geoName || itemName.includes(geoName) || geoName.includes(itemName));
    });
  }

  return found;
};

function MapResizeHandler() {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 300);
    return () => clearTimeout(timer);
  }, [map]);
  return null;
}

function parseCoord(val) {
  if (val === null || val === undefined) return NaN;
  const cleaned = String(val).replace(/,/g, ".").replace(/[\r\n\s]/g, "");
  return parseFloat(cleaned);
}

function parseLatLong(bujurRaw, lintangRaw) {
  const v1 = parseCoord(bujurRaw);
  const v2 = parseCoord(lintangRaw);

  if (isNaN(v1) || isNaN(v2)) return { lat: NaN, lng: NaN };

  if (v1 >= 90 && v1 <= 145 && v2 >= -12 && v2 <= 10) {
    return { lat: v2, lng: v1 };
  }
  if (v2 >= 90 && v2 <= 145 && v1 >= -12 && v1 <= 10) {
    return { lat: v1, lng: v2 };
  }

  return { lat: v1, lng: v2 };
}

export default function MapIndonesiaLeaflet() {
  const [propinsiData, setPropinsiData] = useState([]);
  const [geoSpasialData, setGeoSpasialData] = useState([]);
  const [geoSpasialHACCPData, setGeoSpasialHACCPData] = useState([]);
  const [geoSpasialUPIData, setGeoSpasialUPIData] = useState([]);
  const [uptLocations, setUptLocations] = useState([]);
  const [uptIcon, setUptIcon] = useState(null);

  const { periode } = usePeriode();
  const { startDate, endDate } = periode;


  useEffect(() => {
    const icon = L.divIcon({
      className: "custom-upt-marker",
      html: `
        <div style="position: relative; display: flex; align-items: center; justify-content: center;">
          <div style="position: absolute; width: 18px; height: 18px; border-radius: 50%; background: rgba(37, 99, 235, 0.35); animation: uptPulse 2s infinite ease-in-out;"></div>
          <div style="width: 10px; height: 10px; border-radius: 50%; background: #1d4ed8; border: 2px solid #ffffff; box-shadow: 0 1px 4px rgba(0,0,0,0.4); position: relative; z-index: 1;"></div>
        </div>
      `,
      iconSize: [18, 18],
      iconAnchor: [9, 9],
      popupAnchor: [0, -10],
    });
    setUptIcon(icon);
  }, []);

  useEffect(() => {
    const sDate = String(startDate);
    const eDate = String(endDate);
    fetchPropinsiPivot(sDate, eDate);
    fetchGeoSpasialSKP(sDate, eDate);
    fetchGeoSpasialHACCP(sDate, eDate);
    fetchGeoSpasialUPI(sDate, eDate);
  }, [startDate, endDate]);

  useEffect(() => {
    fetchUPTLocations();
  }, []);

  const fetchPropinsiPivot = async (sDate, eDate) => {
    try {
      const res = await ReportService.getPropinsiIzin(sDate, eDate);
      
      let dataArr = [];
      if (Array.isArray(res)) {
        dataArr = res;
      } else if (Array.isArray(res?.data)) {
        dataArr = res.data;
      } else if (Array.isArray(res?.data?.data)) {
        dataArr = res.data.data;
      }
      
      setPropinsiData(dataArr);
    } catch (err) {
      console.error("Error fetchPropinsiPivot:", err);
      setPropinsiData([]);
    }
  };

  const fetchGeoSpasialSKP = async (sDate, eDate) => {
    try {
      const res = await DashboardService.getGeoSpasialSKP(sDate, eDate);
      setGeoSpasialData(Array.isArray(res) ? res : res?.data ?? []);
    } catch (err) {
      setGeoSpasialData([]);
    }
  };

  const fetchGeoSpasialHACCP = async (sDate, eDate) => {
    try {
      const res = await DashboardService.getGeoSpasialHACCP(sDate, eDate);
      setGeoSpasialHACCPData(Array.isArray(res) ? res : res?.data ?? []);
    } catch (err) {
      setGeoSpasialHACCPData([]);
    }
  };

  const fetchGeoSpasialUPI = async (sDate, eDate) => {
    try {
      const res = await DashboardService.getGeoSpasialUPI(sDate, eDate);
      setGeoSpasialUPIData(Array.isArray(res) ? res : res?.data ?? []);
    } catch (err) {
      setGeoSpasialUPIData([]);
    }
  };

  const fetchUPTLocations = async () => {
    try {
      const res = await DashboardService.getUPTBPPMHKP();
      setUptLocations(Array.isArray(res) ? res : res?.data ?? []);
    } catch (err) {
      setUptLocations([]);
    }
  };


  const validUPTs = useMemo(() => {
    return uptLocations
      .filter((u) => u.STS_AKTIF === 1 || u.STS_AKTIF === undefined)
      .map((u) => {
        const { lat, lng } = parseLatLong(u.BUJUR, u.LINTANG);
        return { ...u, _lat: lat, _lng: lng };
      })
      .filter(
        (u) =>
          !isNaN(u._lat) &&
          !isNaN(u._lng) &&
          u._lat >= -12 && u._lat <= 7 &&
          u._lng >= 94  && u._lng <= 142
      );
  }, [uptLocations]);

  const geoStyle = {
    color: "#2563eb",
    weight: 1.2,
    fillColor: "#3b82f6",
    fillOpacity: 0.25,
  };

  const onEachProvince = (feature, layer) => {
    const props = feature.properties || {};
    const nama = props.PROVINSI ?? props.NM_PROV ?? props.namobj ?? "Provinsi";
    const kodeProvinsi =
      props.KODE_PROV ??
      props.kode_prov ??
      props.KODE ??
      props.kode ??
      props.id ??
      "";
      
    const iconProvinsi = `/image/propinsi/${kodeProvinsi}.png`;

    const p = findDataByFeature(propinsiData, props);
    const s = findDataByFeature(geoSpasialData, props);
    const h = findDataByFeature(geoSpasialHACCPData, props);
    const u = findDataByFeature(geoSpasialUPIData, props);

    layer.on({
      mouseover: (e) => {
        const l = e.target;
        l.setStyle({ fillOpacity: 0.5, weight: 2 });
      },
      mouseout: (e) => {
        const l = e.target;
        l.setStyle(geoStyle);
      },
    });

    layer.bindPopup(`
      <div style="font-family:'Inter', sans-serif; min-width: 230px; padding: 2px;">
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px;">
            <div style="
              width: 32px;
              height: 32px;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            ">
              <img
                src="${iconProvinsi}"
                alt="${nama}"
                style="
                  width: 30px;
                  height: 30px;
                  object-fit: contain;
                  display: block;
                "
              />
          </div>
          <div>
            <div style="font-size: 8px; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px;">PROVINSI</div>
            <div style="font-size: 13px; font-weight: 800; color: #0f172a; line-height: 1.1;">${nama}</div>
          </div>
        </div>

        <div style="background-color: #f8fafc; padding: 4px 6px; border-radius: 4px; font-size: 9px; font-weight: 800; color: #475569; letter-spacing: 0.4px; margin-bottom: 6px; text-transform: uppercase;">
          SERTIFIKASI MUTU PRIMER
        </div>

        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 3px 12px; font-size: 11px; color: #475569; margin-bottom: 10px; padding: 0 2px;">

          <div style="display: flex; justify-content: space-between;">
            <span>CBIB</span>
            <b style="color: #0f172a;">
              ${getNum(p, "CBIB", "cbib")}
            </b>
          </div>

          <div style="display: flex; justify-content: space-between;">
            <span>CDIB</span>
            <b style="color: #0f172a;">
              ${getNum(p, "CDIB", "cdib")}
            </b>
          </div>

          <div style="display: flex; justify-content: space-between;">
            <span>CPIB</span>
            <b style="color: #0f172a;">
              ${getNum(p, "CPIB", "cpib")}
            </b>
          </div>

          
          <div style="display: flex; justify-content: space-between;">
          <span>CPOIB</span>
          <b style="color: #0f172a;">
          ${getNum(p, "CPOIB", "cpoib")}
          </b>
          </div>
          
          <div style="display: flex; justify-content: space-between;">
            <span>CPIB Kapal</span>
            <b style="color: #0f172a;">
              ${getNum(p, "CPIB_Kapal", "CPIB_KAPAL", "cpib_kapal")}
            </b>
          </div>
          <div style="display: flex; justify-content: space-between;">
            <span>CPPIB</span>
            <b style="color: #0f172a;">
              ${getNum(p, "CPPIB", "cppib")}
            </b>
          </div>

        </div>

        <div style="background-color: #f8fafc; padding: 4px 6px; border-radius: 4px; font-size: 9px; font-weight: 800; color: #475569; letter-spacing: 0.4px; margin-bottom: 6px; text-transform: uppercase;">
          SERTIFIKASI MUTU PASCA PANEN
        </div>
        <div style="font-size: 11px; color: #475569; display: flex; flex-direction: column; gap: 4px; padding: 0 2px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="font-weight: 700; color: #334155;">SKP</span>
            <b style="color: #2563eb; font-size: 12px;">${getNum(s, "SKP", "skp", "jml_skp", "JML_SKP")}</b>
          </div>

      

          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="font-weight: 700; color: #334155;">HACCP</span>
            <b style="color: #2563eb; font-size: 12px;">${getNum(h, "jumlah")}</b>
          </div>
          
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 3px 12px; font-size: 10px; color: #64748b; padding-left: 6px; border-left: 2px solid #e2e8f0;">
            <div style="display: flex; justify-content: space-between;"><span>Grade A</span> <b style="color: #0f172a;">${getNum(h, "grade_a", "GRADE_A", "Grade_A")}</b></div>
            <div style="display: flex; justify-content: space-between;"><span>Grade B</span> <b style="color: #0f172a;">${getNum(h, "grade_b", "GRADE_B", "Grade_B")}</b></div>
            <div style="display: flex; justify-content: space-between;"><span>Grade C</span> <b style="color: #0f172a;">${getNum(h, "grade_c", "GRADE_C", "Grade_C")}</b></div>
          </div>

          <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 4px; border-top: 1px dashed #e2e8f0; padding-top: 5px;">
            <span style="font-weight: 700; color: #334155;">Jumlah UPI</span>
            <b style="color: #2563eb; font-size: 12px;">${getNum(u, "jmlUPI", "jmlUpi", "JML_UPI", "jml_upi")}</b>
          </div>
        </div>
      </div>
        `,
          {
            autoPan: false,
            keepInView: false,
            maxWidth: 300,
            offset: L.point(0, -8),
            className: "province-popup",
          }
        );

        layer.on("popupopen", (e) => {
          const popup = e.popup;
          const map = popup._map;
          const popupEl = popup.getElement();

          if (!map || !popupEl) return;

          const point = map.latLngToContainerPoint(
            popup.getLatLng()
          );

          const popupHeight = popupEl.offsetHeight;

          // Tinggi header + sedikit jarak aman
          const headerSafeArea = 145;

          // Cek ruang di atas titik
          const availableAbove = point.y - headerSafeArea;

          if (availableAbove < popupHeight) {
                       popup.options.offset = L.point(
              0,
              popupHeight + 18
            );

            popupEl.classList.add("popup-bottom");
          } else {
            popup.options.offset = L.point(0, -8);

            popupEl.classList.remove("popup-bottom");
          }

          popup.update();
        });
        };

  const geoJsonKey = `${startDate}-${endDate}-${propinsiData.length}-${geoSpasialData.length}-${geoSpasialHACCPData.length}-${geoSpasialUPIData.length}`;

  const boundsIndonesia = [
    [-11.5, 94.0],
    [6.5, 141.5]
  ];

  return (
    <div className="relative w-full overflow-hidden shadow-md rounded-2xl" style={{ height: "600px" }}>
      <style>{`
            @keyframes uptPulse {
              0% {
                transform: scale(1);
                opacity: 0.8;
              }

              70% {
                transform: scale(2.2);
                opacity: 0;
              }

              100% {
                transform: scale(1);
                opacity: 0;
              }
            }

            /* ================================
              POPUP PROVINSI
            ================================= */

            .province-popup .leaflet-popup-content-wrapper {
              border-radius: 14px;
              box-shadow:
                0 10px 30px rgba(15, 23, 42, 0.18),
                0 3px 8px rgba(15, 23, 42, 0.08);
            }

            .province-popup .leaflet-popup-content {
              margin: 12px 14px;
            }

            /* Popup ketika dipindahkan ke bawah */
            .province-popup.popup-bottom
            .leaflet-popup-tip-container {
              top: -20px;
              bottom: auto;
            }

            .province-popup.popup-bottom
            .leaflet-popup-tip {
              margin-top: 11px;
              transform: rotate(225deg);
            }
        `}</style>

      {/* <div
        style={{
          position: "absolute",
          marginTop: "75px",
          top: "18px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          width: "calc(100% - 110px)",
          maxWidth: "780px",
        }}
      >
        <div
          style={{
            background: "rgba(255,255,255,0.94)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            borderRadius: "16px",
            boxShadow: "0 4px 16px rgba(15, 23, 42, 0.15)",
            border: "1px solid rgba(226,232,240,0.9)",
            padding: "14px 18px 14px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "13px",
              minWidth: 0,
              flex: 1,
            }}
          >
            <div
              style={{
                width: "5px",
                height: "32px",
                background: "#1769ff",
                borderRadius: "10px",
                flexShrink: 0,
              }}
            />

            <div
              style={{
                fontSize: "17px",
                fontWeight: 800,
                color: "#1e293b",
                lineHeight: 1.25,
                letterSpacing: "-0.3px",
              }}
            >
              <div>
                PETA SEBARAN UPT DAN PENERBITAN SERTIFIKASI MUTU
              </div>

              <div>
                <span style={{ color: "#1268ff" }}>BPP</span>
                <span style={{ color: "#ff7a00" }}>MHKP</span>
              </div>
            </div>
          </div>

        </div>
      </div> */}

      <MapContainer
        center={[-2.5, 118]}
        zoom={5}
        minZoom={5}                     
        maxZoom={18}                    
        maxBounds={boundsIndonesia}     
        maxBoundsViscosity={1.0}        
        style={{ height: "100%", width: "100%" }}
        zoomControl={true}
      >
        <MapResizeHandler />
        
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          // attribution='&copy; OpenStreetMap contributors'
        />

        <GeoJSON
          key={geoJsonKey}
          data={indonesiaGeoJson}
          style={() => geoStyle}
          onEachFeature={onEachProvince}
        />

        {uptIcon && validUPTs.map((upt) => (
          <Marker key={upt.KD_UNIT} position={[upt._lat, upt._lng]} icon={uptIcon}>
            <Popup>
              <div style={{ fontFamily: "'Inter', sans-serif" }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: "#2563eb", textTransform: "uppercase" }}>
                  UPT BPPMHKP
                </div>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>
                  {upt.NM_PENDEK_BARU}
                </div>
                <div style={{ fontSize: 10, color: "#64748b" }}>
                  <span style={{ fontWeight: 600 }}>📍 </span>
                  {upt.KOTA}
                </div>
                <div style={{ fontSize: 10, color: "#94a3b8", lineHeight: 1.4 }}>
                    {String(upt.ALAMAT_UNIT).replace(/[\r\n]/g, " ").trim()}
                  </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}